import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import React, { useState } from 'react';
import { Box, Button, Grid, Menu, MenuItem, Typography, Tooltip, Stack } from '@mui/material';
import PropTypes from 'prop-types';
import { HOST_API_URL } from '../../../config-global';
import { DateRangePickerSelect } from '../../../reusable-components/date-range-picker';
import Iconify from '../../../reusable-components/iconify';
import { useDispatch, useSelector } from '../../../redux/store';
import { getInvoiceExcel } from '../../../redux/slices/invoicing';
import { fCurrency } from '../../../utils/formatNumber';
import ProcessManualInvoices from './ManualInvoices/ProcessManualInvoices';
import SubmitSelectedInvoices from './ManualInvoices/SubmitSelectedInvoices';
import FindAndReplace from './FindAndReplace';
import UploadPDF from './UploadPDF';
import UploadStatementPDF from './UploadStatementPDF';
import NewVendorModal from './Vendors/NewVendorModal';
import UploadManualInvoiceButtons from './ManualInvoices/UploadManualInvoiceButtons';
import { useSnackbar, closeSnackbar } from '../../../reusable-components/snackbar';
import { processFailedInvoices } from '../../../api/invoicing';
import DropdownAction from '../../../reusable-components/dropdown-action/DropdownAction';
import DropdownExpandableAction from '../../../reusable-components/dropdown-action/DropdownExpandableAction';

CustomToolbar.propTypes = {
  isFiltered: PropTypes.func,
  activeRows: PropTypes.array,
  showSelection: PropTypes.string,
  setShowSelection: PropTypes.func,
  openInvoiceUploader: PropTypes.bool,
  setOpenInvoiceUploader: PropTypes.func,
  setIsLoading: PropTypes.func,
};
export default function CustomToolbar({
  isFiltered,
  activeRows,
  showSelection,
  setShowSelection,
  setOpenInvoiceUploader,
  openInvoiceUploader,
  setIsLoading,
}) {
  const location = useLocation();
  const section = location.pathname.split('/').pop();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [isReplaceDropdownOpen, setIsReplaceDropdownOpen] = useState(false);
  const [isDownloadOptionOpen, setIsDownloadOptionOpen] = useState(false);
  const [isReplaceOptionOpen, setIsReplaceOptionOpen] = useState(false);
  const [replaceType, setReplaceType] = useState('Manager');
  const [showUploadPDF, setShowUploadPDF] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    data: {
      manualInvoicing,
      pendingInvoicing,
      pendingFilterInvoicing,
      invoicesAwaitingSubmission,
      rejectedInvoices,
      invoicesInQueue,
      previousInvoicing,
    },
  } = useSelector((state) => state.invoicing);
  const { user } = useSelector((state) => state.user);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const downloadFile = async (downloadType) => {
    const date = { Start: searchParams.get('start'), End: searchParams.get('end') };
    const invoices =
      section === 'new'
        ? manualInvoicing
        : section === 'pending'
        ? pendingInvoicing
        : section === 'pendingFilter'
        ? pendingFilterInvoicing
        : section === 'awaitingSubmission'
        ? invoicesAwaitingSubmission
        : section === 'invoicesInQueue'
        ? invoicesInQueue
        : section === 'rejectedInvoices'
        ? rejectedInvoices
        : previousInvoicing;

    const key = enqueueSnackbar('Preparing your invoices download...', {
      variant: 'info',
      persist: true,
    });
    try {
      await dispatch(getInvoiceExcel(section, invoices, date, downloadType));
      enqueueSnackbar('Invoices downloaded successfully!', {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar('Failed to download invoices. Please try again.', {
        variant: 'error',
      });
    } finally {
      closeSnackbar(key);
    }
  };
  const downloadStatements = async () => {
    window.open(`${HOST_API_URL}Api/Invoices/DownloadInvoiceStatements`, '_blank');
  };

  function DownloadDropdown({ onClose }) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', width: '100%' }}>
        <DropdownAction
          onClick={() => {
            downloadFile('invoice');
            onClose?.();
          }}
        >
          Download Invoices
        </DropdownAction>

        <DropdownAction
          onClick={() => {
            downloadFile('gl');
            onClose?.();
          }}
        >
          Download Invoice GLs
        </DropdownAction>
      </Box>
    );
  }

  return (
    <>
      <Grid container direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
        <Grid item>
          <Grid container direction="row" alignItems="center" spacing={1}>
            {!isFiltered('vendors') ? (
              <>
                {isFiltered('new') && showSelection !== 'Select invoices' && (
                  <>
                    {user?.email === 'reisenberg@platformsolutions.com' && (
                      <Grid item>
                        <Button
                          variant="contained"
                          color="secondary"
                          size="small"
                          startIcon={<Iconify icon="bx:refresh" />}
                          onClick={async () => {
                            const processInvoices = await processFailedInvoices();
                            if (processInvoices.status === 200 && processInvoices.data.length > 0) {
                              localStorage.setItem('selectedInvoices', JSON.stringify(processInvoices.data || []));
                              const nextId = JSON.parse(localStorage.getItem('selectedInvoices'))[0];
                              navigate(`/dashboard/invoicing/${section}/${nextId}`);
                            }
                          }}
                        >
                          Process Failed Invoices
                        </Button>
                      </Grid>
                    )}
                    <Grid item>
                      <UploadManualInvoiceButtons
                        setOpenInvoiceUploader={setOpenInvoiceUploader}
                        openInvoiceUploader={openInvoiceUploader}
                      />
                    </Grid>
                  </>
                )}
                {(isFiltered('new') || isFiltered('pending') || isFiltered('pendingFilter')) && (
                  <>
                    <Grid item>
                      <ProcessManualInvoices
                        setShowSelection={setShowSelection}
                        showSelection={showSelection}
                        setIsLoading={setIsLoading}
                      />
                    </Grid>
                  </>
                )}

                {(isFiltered('awaitingSubmission') ||
                  isFiltered('invoicesInQueue') ||
                  isFiltered('rejectedInvoices')) && (
                  <>
                    <Grid item>
                      <SubmitSelectedInvoices
                        setShowSelection={setShowSelection}
                        showSelection={showSelection}
                        activeRows={activeRows}
                      />
                    </Grid>
                  </>
                )}
                {isFiltered('previous') && (
                  <Grid item>
                    <DateRangePickerSelect
                      pickerSx={{
                        height: '30px',
                        '& .MuiOutlinedInput-root': { height: '30px', minHeight: '30px' },
                      }}
                      buttonSx={{ mt: 0 }}
                    />
                  </Grid>
                )}
                {!showSelection &&
                  (isFiltered('new') ||
                  isFiltered('previous') ||
                  isFiltered('pendingFilter') ||
                  isFiltered('awaitingSubmission') ||
                  isFiltered('invoicesInQueue') ||
                  isFiltered('rejectedInvoices') ? (
                    <Grid item>
                      <Box sx={{ display: 'inline-block', mr: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          color="secondary"
                          onClick={handleClick}
                          endIcon={<Iconify icon="mdi:arrow-down-drop" />}
                        >
                          Download
                        </Button>
                      </Box>
                      <Menu
                        id="basic-menu"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        slotProps={{
                          paper: {
                            sx: {
                              width: 245,
                              p: 1,
                              borderRadius: '5px',
                              boxShadow: '0px 2px 6px rgba(0,0,0,.06), 0px 8px 24px rgba(0,0,0,.12)',
                            },
                          },
                          list: {
                            sx: {
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '14px',
                              p: 0,
                            },
                            'aria-labelledby': 'basic-button',
                          },
                        }}
                      >
                        <MenuItem
                          disableRipple
                          disableGutters
                          sx={{ p: 0, minHeight: 'unset' }}
                          onClick={() => {
                            downloadFile('invoice');
                            handleClose();
                          }}
                        >
                          <DropdownAction badgeIcon="material-symbols:download">Download Invoices</DropdownAction>
                        </MenuItem>

                        <MenuItem
                          disableRipple
                          disableGutters
                          sx={{ p: 0, minHeight: 'unset' }}
                          onClick={() => {
                            downloadFile('gl');
                            handleClose();
                          }}
                        >
                          <DropdownAction badgeIcon="material-symbols:download">Download Invoice GLs</DropdownAction>
                        </MenuItem>
                      </Menu>
                    </Grid>
                  ) : (
                    !isFiltered('new') &&
                    !isFiltered('reports') &&
                    !isFiltered('awaitingSubmission') &&
                    !isFiltered('invoicesInQueue') &&
                    !isFiltered('rejectedInvoices') &&
                    !isFiltered('pending') &&
                    !isFiltered('pendingFilter') && (
                      <Grid item>
                        <Box sx={{ display: 'inline-block', mr: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            color="secondary"
                            onClick={downloadStatements}
                            startIcon={<Iconify icon="file-icons:microsoft-excel" />}
                          >
                            Download Statements
                          </Button>
                        </Box>
                        <UploadStatementPDF />
                      </Grid>
                    )
                  ))}
                {isFiltered('pending') && !showSelection && (
                  <Grid item>
                    <Box sx={{ display: 'inline-block', mr: 1 }}>
                      <Tooltip title="More actions">
                        <Button
                          variant="outlined"
                          size="small"
                          color="secondary"
                          sx={{ minWidth: 0, py: 0.5 }}
                          onClick={(e) => setActionsAnchorEl(e.currentTarget)}
                        >
                          <Iconify icon="mdi:more-vert" sx={{ fontSize: 22 }} />
                        </Button>
                      </Tooltip>
                    </Box>
                    <Menu
                      anchorEl={actionsAnchorEl}
                      open={Boolean(actionsAnchorEl)}
                      onClose={() => {
                        setActionsAnchorEl(null);
                        setIsDownloadOptionOpen(false);
                        setIsReplaceDropdownOpen(false);
                      }}
                      slotProps={{
                        paper: {
                          sx: {
                            width: 250,
                            p: 1,
                            borderRadius: '5px',
                            boxShadow: '0px 2px 6px rgba(0,0,0,.06), 0px 8px 24px rgba(0,0,0,.12)',
                            '& .MuiMenu-list': {
                              p: 0,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '14px',
                            },
                          },
                        },
                      }}
                    >
                      <DropdownExpandableAction
                        icon="material-symbols:download"
                        onExpandedChange={setIsDownloadOptionOpen}
                        panel={
                          <DownloadDropdown
                            onClose={() => {
                              setActionsAnchorEl(null);
                              setIsDownloadOptionOpen(false);
                              setIsReplaceDropdownOpen(false);
                            }}
                          />
                        }
                      >
                        Download
                      </DropdownExpandableAction>

                      <MenuItem
                        disableRipple
                        disableGutters
                        sx={{
                          p: 0,
                          minHeight: 'unset',
                          '&:hover': { bgcolor: 'transparent' },
                        }}
                        onClick={() => {
                          setShowUploadPDF(true);
                          setActionsAnchorEl(null);
                          setIsDownloadOptionOpen(false);
                          setIsReplaceDropdownOpen(false);
                        }}
                      >
                        <Tooltip title="For EDI invoices" arrow>
                          <Box sx={{ width: '100%' }}>
                            <DropdownAction badgeIcon="mdi:tray-upload">Upload PDF</DropdownAction>
                          </Box>
                        </Tooltip>
                      </MenuItem>

                      <DropdownExpandableAction
                        icon="mdi:find-replace"
                        onExpandedChange={setIsReplaceDropdownOpen}
                        panel={
                          <Stack spacing={1} sx={{ pl: 5 }}>
                            <Box
                              onClick={() => {
                                setReplaceType('Manager');
                                setIsReplaceOptionOpen(true);
                                setActionsAnchorEl(null);
                                setIsDownloadOptionOpen(false);
                                setIsReplaceDropdownOpen(false);
                              }}
                              sx={{
                                display: 'flex',
                                height: 30,
                                alignItems: 'center',
                                gap: '10px',
                                alignSelf: 'stretch',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                color: 'grey.700',
                                '&:hover': { color: 'text.primary' },
                              }}
                            >
                              Manager
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                height: 30,
                                alignItems: 'center',
                                gap: '10px',
                                alignSelf: 'stretch',
                                cursor: 'not-allowed',
                                fontSize: '0.875rem',
                                color: 'grey.400',
                              }}
                            >
                              Reviewer
                            </Box>
                          </Stack>
                        }
                      >
                        Replace
                      </DropdownExpandableAction>
                    </Menu>
                  </Grid>
                )}
                {!isFiltered('reports') && (
                  <Grid item>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Typography>
                        Total: 
                      </Typography>
                      <Typography variant="subtitle1" sx={{ color: 'info.main' }}>
                        {isFiltered('pending') ||
                        isFiltered('new') ||
                        isFiltered('pendingFilter') ||
                        isFiltered('awaitingSubmission') ||
                        isFiltered('invoicesInQueue') ||
                        isFiltered('rejectedInvoices')
                          ? fCurrency(activeRows.map((item) => item.total).reduce((a, b) => a + b, 0))
                          : isFiltered('previous')
                          ? fCurrency(
                              activeRows
                                .filter((item) => item.deleteFlag !== true)
                                .map((item) => item.total)
                                .reduce((a, b) => a + b, 0)
                            )
                          : isFiltered('incomplete')
                          ? fCurrency(
                              activeRows
                                ?.filter((st) => st.complete === false)
                                ?.map((item) => item.total)
                                ?.reduce((a, b) => a + b, 0)
                            )
                          : fCurrency(
                              activeRows
                                ?.filter((st) => st.complete === true)
                                ?.map((item) => item.total)
                                ?.reduce((a, b) => a + b, 0)
                            )}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </>
            ) : (
              <Grid item>
                <NewVendorModal />
                <Button
                  variant="outlined"
                  size="small"
                  color="secondary"
                  onClick={() => {
                    window.open(`${HOST_API_URL}Api/Invoices/DownloadVendors`, '_blank');
                  }}
                  startIcon={<Iconify icon="file-icons:microsoft-excel" />}
                >
                  Download
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
      {/* FindAndReplace and UploadPDF modal rendered at root for proper overlay */}
      <FindAndReplace
        open={isReplaceOptionOpen}
        onClose={() => setIsReplaceOptionOpen(false)}
        replaceType={replaceType}
      />
      <UploadPDF open={showUploadPDF} onClose={() => setShowUploadPDF(false)} />
    </>
  );
}
